import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PublicPageShell from '../components/PublicPageShell';
import { AI_RAMS_DISCLAIMER } from '../components/LegalBusinessDetails';

const PRIMARY = '#0087c1';

/* ── Shared mockup chrome ── */
const mockupFont = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const mc = { bg: '#fff', border: '#e5e7eb', muted: '#94a3b8', text: '#1e293b', sub: '#64748b', primary: PRIMARY, green: '#16a34a', amber: '#d97706' };

const FeatureMockup = ({ title, description, align, children }) => {
  const theme = useTheme();
  const isLeft = align === 'left';
  return (
    <Grid container spacing={4} alignItems="center" sx={{ mb: { xs: 6, md: 10 } }} direction={isLeft ? 'row' : 'row-reverse'}>
      <Grid item xs={12} md={5}>
        <Typography variant="h5" component="h3" fontWeight={700} sx={{ mb: 1.5, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
          {description}
        </Typography>
      </Grid>
      <Grid item xs={12} md={7}>
        <Box sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
          background: mc.bg,
        }}>
          {children}
        </Box>
      </Grid>
    </Grid>
  );
};

/* ── Dashboard mockup ── */
const DashboardMockup = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    {/* Mini topbar */}
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontWeight: 800, color: mc.primary, fontSize: 14 }}>struxor</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ px: 1.5, py: 0.5, background: '#f1f5f9', borderRadius: 1, fontSize: 11, color: mc.muted }}>Search projects…</Box>
        <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
      </Box>
    </Box>
    {/* Stats */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, p: 2 }}>
      {[
        { label: 'Active Quotes', value: '12', color: mc.amber },
        { label: 'Sales Orders', value: '8', color: mc.green },
        { label: 'Completed', value: '23', color: mc.primary },
        { label: 'Open Activities', value: '34', color: '#7c3aed' },
      ].map((s) => (
        <Box key={s.label} sx={{ background: '#fafbfc', borderRadius: 2, p: 1.5, border: `1px solid ${mc.border}` }}>
          <Box sx={{ fontSize: 10, color: mc.muted, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>{s.label}</Box>
          <Box sx={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</Box>
        </Box>
      ))}
    </Box>
    {/* Activity rows */}
    <Box sx={{ px: 2, pb: 2 }}>
      <Box sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Upcoming Activities</Box>
      {[
        { title: 'Install perimeter fencing — Section A', project: 'Riverside Development', badge: 'Due soon', badgeColor: '#fef3c7', badgeText: '#92400e' },
        { title: 'Groundworks inspection sign-off', project: 'Elm Court', badge: 'Overdue', badgeColor: '#fee2e2', badgeText: '#991b1b' },
        { title: 'Deliver hoarding panels to site', project: 'Park Lane Phase 2', badge: 'Due soon', badgeColor: '#fef3c7', badgeText: '#92400e' },
      ].map((a) => (
        <Box key={a.title} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: `1px solid #f1f5f9` }}>
          <Box>
            <Box sx={{ fontSize: 12, fontWeight: 500 }}>{a.title}</Box>
            <Box sx={{ fontSize: 10, color: mc.muted }}>{a.project}</Box>
          </Box>
          <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: a.badgeColor, color: a.badgeText, whiteSpace: 'nowrap' }}>{a.badge}</Box>
        </Box>
      ))}
    </Box>
  </Box>
);

/* ── AI Assistant mockup ── */
const AIMockup = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontWeight: 600, fontSize: 13 }}>AI Assistant</Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 260 }}>
      {/* User message 1 */}
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '80%', background: mc.primary, color: '#fff', borderRadius: '12px 12px 4px 12px', px: 2, py: 1.2, fontSize: 12, lineHeight: 1.5 }}>
        When can we next fit in a 3 day project?
      </Box>
      {/* Assistant message 1 */}
      <Box sx={{ alignSelf: 'flex-start', maxWidth: '88%' }}>
        <Box sx={{ background: '#f8fafc', border: `1px solid ${mc.border}`, borderRadius: '12px 12px 12px 4px', px: 2, py: 1.2, fontSize: 12, lineHeight: 1.6 }}>
          Based on your current schedule, you're booked through <strong>Monday 9 March 2026</strong>. The next available 3-day slot is <strong>Tuesday 10 March to Thursday 12 March 2026</strong>.
          <Box sx={{ mt: 0.8 }}>Would you like me to pencil that in, or would you prefer Wednesday 11 to Friday 13 March? If Saturdays are an option, let me know and I can offer alternative slots too.</Box>
        </Box>
      </Box>
      {/* User message 2 */}
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '80%', background: mc.primary, color: '#fff', borderRadius: '12px 12px 4px 12px', px: 2, py: 1.2, fontSize: 12, lineHeight: 1.5 }}>
        Yeah, please block out from Tuesday for riverside project
      </Box>
      {/* Assistant message 2 */}
      <Box sx={{ alignSelf: 'flex-start', maxWidth: '88%' }}>
        <Box sx={{ background: '#f8fafc', border: `1px solid ${mc.border}`, borderRadius: '12px 12px 12px 4px', px: 2, py: 1.2, fontSize: 12, lineHeight: 1.6 }}>
          <Box sx={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2, p: 1.2, mb: 1, fontSize: 11 }}>
            <Box sx={{ fontWeight: 600, color: mc.green, mb: 0.3 }}>✓ Schedule blocked — Riverside Development</Box>
            <Box sx={{ color: mc.sub }}>Tue 10 Mar – Thu 12 Mar 2026 · 3 days</Box>
          </Box>
          All set. Want me to add any other crew, book materials/deliveries, or notify the client?
        </Box>
      </Box>
    </Box>
    {/* Input */}
    <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 2, alignItems: 'center' }}>
      <Box sx={{ flex: 1, background: '#f8fafc', border: `1px solid ${mc.border}`, borderRadius: 2, px: 1.5, py: 1, fontSize: 11, color: mc.muted }}>Type your message…</Box>
      <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: mc.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Box sx={{ width: 0, height: 0, borderLeft: '6px solid #fff', borderTop: '5px solid transparent', borderBottom: '5px solid transparent', ml: '2px' }} />
      </Box>
    </Box>
  </Box>
);

/* ── RAMS Signature Collection mockup ── */
const RAMSSignatureMockup = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontSize: 11, color: mc.muted }}>RAMS / <span style={{ color: mc.text, fontWeight: 600 }}>Riverside Development</span></Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    {/* Document header */}
    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
      <Box sx={{ fontSize: 14, fontWeight: 700, mb: 0.3 }}>Perimeter Fencing — Risk Assessment</Box>
      <Box sx={{ fontSize: 10, color: mc.muted, mb: 1 }}>RAMS-042 · Issued 8 Mar 2026 · v1.2</Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#dcfce7', color: '#166534' }}>3 Signed</Box>
        <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#fef3c7', color: '#92400e' }}>2 Pending</Box>
      </Box>
    </Box>
    {/* Team member sign-off list */}
    <Box sx={{ px: 2, pb: 1 }}>
      <Box sx={{ fontSize: 11, fontWeight: 600, color: mc.sub, mb: 0.8 }}>Team Member Sign-off</Box>
      {[
        { name: 'James Carter', role: 'Site Supervisor', signed: true, time: '08:12' },
        { name: 'Danny Walsh', role: 'Fencing Operative', signed: true, time: '08:15' },
        { name: 'Mark Ellis', role: 'Fencing Operative', signed: true, time: '08:18' },
        { name: 'Tom Brady', role: 'Groundworks', signed: false },
        { name: 'Steve Hunt', role: 'Groundworks', signed: false },
      ].map((w) => (
        <Box key={w.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 22, height: 22, borderRadius: '50%', background: w.signed ? '#dcfce7' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: w.signed ? mc.green : mc.muted, flexShrink: 0 }}>{w.signed ? '✓' : w.name[0]}</Box>
            <Box>
              <Box sx={{ fontSize: 11, fontWeight: 500 }}>{w.name}</Box>
              <Box sx={{ fontSize: 9, color: mc.muted }}>{w.role}</Box>
            </Box>
          </Box>
          {w.signed
            ? <Box sx={{ fontSize: 9, color: mc.green, fontWeight: 600 }}>Signed {w.time}</Box>
            : <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: mc.primary, color: '#fff', cursor: 'pointer' }}>Sign Now</Box>
          }
        </Box>
      ))}
    </Box>
    {/* Signature capture prompt */}
    <Box sx={{ mx: 2, mb: 2, mt: 0.5, borderRadius: 2, border: `1px solid ${mc.border}`, background: '#fafbfc', p: 1.5 }}>
      <Box sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>Tom Brady — Sign below</Box>
      <Box sx={{ height: 48, borderRadius: 1.5, border: '1.5px dashed #cbd5e1', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ fontSize: 10, color: mc.muted }}>Draw signature here</Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Box sx={{ flex: 1, textAlign: 'center', py: 0.6, borderRadius: 1, border: `1px solid ${mc.border}`, fontSize: 10, color: mc.sub, cursor: 'pointer' }}>Clear</Box>
        <Box sx={{ flex: 2, textAlign: 'center', py: 0.6, borderRadius: 1, background: mc.primary, color: '#fff', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Confirm Signature</Box>
      </Box>
    </Box>
  </Box>
);

/* ── RAMS Generator (AI) mockup ── */
const RAMSGeneratorMockup = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontWeight: 600, fontSize: 13 }}>AI Assistant</Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 260 }}>
      {/* User message */}
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '80%', background: mc.primary, color: '#fff', borderRadius: '12px 12px 4px 12px', px: 2, py: 1.2, fontSize: 12, lineHeight: 1.5 }}>
        Generate a RAMS for the Riverside Development perimeter fencing installation
      </Box>
      {/* Assistant response */}
      <Box sx={{ alignSelf: 'flex-start', maxWidth: '90%' }}>
        <Box sx={{ background: '#f8fafc', border: `1px solid ${mc.border}`, borderRadius: '12px 12px 12px 4px', px: 2, py: 1.2, fontSize: 12, lineHeight: 1.6 }}>
          Done! RAMS created for <strong>Riverside Development — Perimeter Fencing</strong>.
          <Box sx={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 2, p: 1.5, mt: 1, fontSize: 11 }}>
            <Box sx={{ fontWeight: 600, color: mc.primary, fontSize: 11, mb: 0.8 }}>✓ RAMS-043 Generated</Box>
            {[
              { label: 'Hazard', value: 'Manual handling — heavy posts & panels' },
              { label: 'Hazard', value: 'Working near live carriageway' },
              { label: 'Hazard', value: 'Struck by vehicle or plant' },
              { label: 'Control', value: 'PPE: hard hat, hi-vis, steel toe boots' },
              { label: 'Control', value: 'Banksman required during deliveries' },
            ].map((r, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 0.8, py: 0.3, borderTop: i === 0 ? 'none' : '1px solid #e0f2fe', alignItems: 'flex-start' }}>
                <Box sx={{ fontSize: 9, fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 0.5, background: r.label === 'Hazard' ? '#fee2e2' : '#dcfce7', color: r.label === 'Hazard' ? '#991b1b' : '#166534', mt: 0.2, flexShrink: 0 }}>{r.label}</Box>
                <Box sx={{ fontSize: 10, color: mc.sub, lineHeight: 1.4 }}>{r.value}</Box>
              </Box>
            ))}
            <Box sx={{ mt: 1, pt: 0.5, borderTop: '1px solid #bae6fd', display: 'flex', gap: 1 }}>
              <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: mc.primary, color: '#fff', cursor: 'pointer' }}>View Full RAMS</Box>
              <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, border: `1px solid ${mc.border}`, color: mc.sub, cursor: 'pointer' }}>Export PDF</Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
    {/* Input */}
    <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 2, alignItems: 'center' }}>
      <Box sx={{ flex: 1, background: '#f8fafc', border: `1px solid ${mc.border}`, borderRadius: 2, px: 1.5, py: 1, fontSize: 11, color: mc.muted }}>Type your message…</Box>
      <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: mc.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Box sx={{ width: 0, height: 0, borderLeft: '6px solid #fff', borderTop: '5px solid transparent', borderBottom: '5px solid transparent', ml: '2px' }} />
      </Box>
    </Box>
  </Box>
);

/* ── Project detail mockup ── */
const ProjectMockup = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontSize: 11, color: mc.muted }}>Projects / <span style={{ color: mc.text, fontWeight: 600 }}>Riverside Development</span></Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    {/* Project header */}
    <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ fontSize: 16, fontWeight: 700 }}>Riverside Development</Box>
      <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#dcfce7', color: '#166534' }}>Sales Order</Box>
    </Box>
    <Box sx={{ px: 2, pb: 1, fontSize: 10, color: mc.muted }}>P-1042 · Perimeter Fencing · 45 Thames Road, London</Box>
    {/* Tabs */}
    <Box sx={{ display: 'flex', gap: 0, px: 2, borderBottom: `1px solid ${mc.border}`, overflowX: 'auto' }}>
      {['Quote', 'Details', 'Activities', 'Team', 'Documents', 'Forms', 'RAMS', 'POs'].map((t, i) => (
        <Box key={t} sx={{ px: 1.2, py: 1, fontSize: 11, fontWeight: i === 0 ? 600 : 400, color: i === 0 ? mc.primary : mc.sub, borderBottom: i === 0 ? `2px solid ${mc.primary}` : '2px solid transparent', whiteSpace: 'nowrap', cursor: 'pointer' }}>{t}</Box>
      ))}
    </Box>
    {/* Items table */}
    <Box sx={{ px: 2, pt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ fontSize: 13, fontWeight: 600 }}>Quote Items</Box>
        <Box sx={{ fontSize: 10, fontWeight: 600, color: '#fff', background: mc.primary, px: 1.2, py: 0.3, borderRadius: 1, cursor: 'pointer' }}>+ Add Item</Box>
      </Box>
      {/* Header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 0.5fr 0.5fr 0.7fr 0.8fr', gap: 1, py: 0.8, fontSize: 9, fontWeight: 600, color: mc.muted, textTransform: 'uppercase', borderBottom: `1px solid ${mc.border}` }}>
        <Box>Description</Box><Box>Qty</Box><Box>Unit</Box><Box>Rate</Box><Box sx={{ textAlign: 'right' }}>Total</Box>
      </Box>
      {[
        { desc: '2.0m High V-Mesh Weldmesh', qty: '180', unit: 'm', rate: '£80.00', total: '£14,400.00' },
        { desc: 'Additional Posts', qty: '2', unit: 'each', rate: '£100.00', total: '£200.00' },
        { desc: 'UPVC Hoarding on Ground', qty: '250', unit: 'm', rate: '£160.00', total: '£40,000.00' },
      ].map((item) => (
        <Box key={item.desc} sx={{ display: 'grid', gridTemplateColumns: '2fr 0.5fr 0.5fr 0.7fr 0.8fr', gap: 1, py: 1, fontSize: 11, borderBottom: `1px solid #f1f5f9`, alignItems: 'center' }}>
          <Box sx={{ fontWeight: 500 }}>{item.desc}</Box><Box>{item.qty}</Box><Box>{item.unit}</Box><Box>{item.rate}</Box><Box sx={{ textAlign: 'right', fontWeight: 600 }}>{item.total}</Box>
        </Box>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1.5, borderTop: `2px solid ${mc.border}`, mt: 0.5 }}>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ fontSize: 14, fontWeight: 700 }}>Net Total: £54,600.00</Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

/* ── Timesheet mockup ── */
const TimesheetMockup = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontWeight: 600, fontSize: 13 }}>My Time</Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    {/* Clock status */}
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mx: 2, mt: 1.5, borderRadius: 2, border: `1px solid ${mc.border}`, background: '#fafbfc' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: mc.green }} />
        <Box>
          <Box sx={{ fontSize: 12, fontWeight: 600 }}>Currently clocked in</Box>
          <Box sx={{ fontSize: 10, color: mc.muted }}>Since 07:45 AM</Box>
        </Box>
      </Box>
      <Box sx={{ fontSize: 20, fontWeight: 700, color: mc.primary }}>4h 32m</Box>
      <Box sx={{ fontSize: 11, fontWeight: 600, px: 1.5, py: 0.5, borderRadius: 1, background: '#fee2e2', color: '#dc2626', cursor: 'pointer' }}>Clock Out</Box>
    </Box>
    {/* Week header */}
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
      <Box sx={{ fontSize: 11, color: mc.sub, cursor: 'pointer' }}>←</Box>
      <Box sx={{ fontSize: 13, fontWeight: 600 }}>3 Mar – 9 Mar 2026</Box>
      <Box sx={{ fontSize: 11, color: mc.sub, cursor: 'pointer' }}>→</Box>
    </Box>
    {/* Table */}
    <Box sx={{ px: 2, pb: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.8fr 0.8fr', gap: 1, py: 0.8, fontSize: 9, fontWeight: 600, color: mc.muted, textTransform: 'uppercase', borderBottom: `1px solid ${mc.border}` }}>
        <Box>Day</Box><Box>In</Box><Box>Out</Box><Box>Hours</Box><Box>Status</Box>
      </Box>
      {[
        { day: 'Monday', d: '3 Mar', i: '07:30', o: '16:30', h: '8h 30m', status: 'Approved', sc: '#dcfce7', st: '#166534' },
        { day: 'Tuesday', d: '4 Mar', i: '07:45', o: '17:00', h: '8h 45m', status: 'Approved', sc: '#dcfce7', st: '#166534' },
        { day: 'Wednesday', d: '5 Mar', i: '08:00', o: '16:00', h: '7h 30m', status: 'Pending', sc: '#fef3c7', st: '#92400e' },
        { day: 'Thursday', d: '6 Mar', i: '07:15', o: '16:45', h: '9h 0m', status: 'Pending', sc: '#fef3c7', st: '#92400e' },
        { day: 'Friday', d: '7 Mar', i: '07:45', o: '—', h: '4h 32m', status: 'Active', sc: '#e0f2fe', st: mc.primary },
      ].map((r) => (
        <Box key={r.day} sx={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.8fr 0.8fr', gap: 1, py: 0.8, fontSize: 11, borderBottom: `1px solid #f1f5f9`, alignItems: 'center' }}>
          <Box><Box sx={{ fontWeight: 500 }}>{r.day}</Box><Box sx={{ fontSize: 9, color: mc.muted }}>{r.d}</Box></Box>
          <Box>{r.i}</Box><Box>{r.o}</Box>
          <Box sx={{ fontWeight: 600, color: mc.primary }}>{r.h}</Box>
          <Box><Box component="span" sx={{ fontSize: 9, fontWeight: 600, px: 0.8, py: 0.2, borderRadius: 1, background: r.sc, color: r.st }}>{r.status}</Box></Box>
        </Box>
      ))}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.8fr 0.8fr', gap: 1, py: 1, fontSize: 12, borderTop: `2px solid ${mc.border}`, mt: 0.5, fontWeight: 700 }}>
        <Box sx={{ gridColumn: 'span 3', textAlign: 'right', pr: 1 }}>Weekly Total</Box>
        <Box sx={{ color: mc.primary }}>38h 17m</Box>
        <Box />
      </Box>
    </Box>
  </Box>
);

/* ── Timeline Dashboard mockup ── */
const TimelineDashboardPreview = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontWeight: 800, color: mc.primary, fontSize: 14 }}>struxor</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ px: 1.5, py: 0.5, background: '#f1f5f9', borderRadius: 1, fontSize: 11, color: mc.muted }}>Search projects…</Box>
        <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
      </Box>
    </Box>
    <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ fontSize: 13, fontWeight: 700 }}>Timeline</Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Box sx={{ fontSize: 10, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#e0f2fe', color: mc.primary, cursor: 'pointer' }}>Week</Box>
        <Box sx={{ fontSize: 10, fontWeight: 500, px: 1, py: 0.3, borderRadius: 1, color: mc.muted, cursor: 'pointer' }}>Month</Box>
      </Box>
    </Box>
    <Box sx={{ px: 2, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ fontSize: 11, color: mc.sub, cursor: 'pointer' }}>← Prev</Box>
      <Box sx={{ fontSize: 12, fontWeight: 600 }}>10 – 14 Mar 2026</Box>
      <Box sx={{ fontSize: 11, color: mc.sub, cursor: 'pointer' }}>Next →</Box>
    </Box>
    {/* Day columns */}
    <Box sx={{ px: 2, pt: 1, pb: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 1 }}>
        {['Mon 10', 'Tue 11', 'Wed 12', 'Thu 13', 'Fri 14'].map(d => (
          <Box key={d} sx={{ textAlign: 'center', fontSize: 9, fontWeight: 600, color: mc.muted, textTransform: 'uppercase', py: 0.5 }}>{d}</Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, minHeight: 140 }}>
        {/* Mon */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#dbeafe', borderLeft: `3px solid ${mc.primary}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: mc.primary }}>Flooring repair</Box>
            <Box sx={{ fontSize: 8, color: mc.sub }}>Riverside Dev</Box>
          </Box>
          <Box sx={{ background: '#fef3c7', borderLeft: '3px solid #d97706', borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#92400e' }}>Safety audit</Box>
            <Box sx={{ fontSize: 8, color: mc.sub }}>Elm Court</Box>
          </Box>
        </Box>
        {/* Tue */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#dbeafe', borderLeft: `3px solid ${mc.primary}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: mc.primary }}>Fencing install</Box>
            <Box sx={{ fontSize: 8, color: mc.sub }}>Riverside Dev</Box>
          </Box>
        </Box>
        {/* Wed */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#dcfce7', borderLeft: `3px solid ${mc.green}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#166534' }}>Hoarding delivery</Box>
            <Box sx={{ fontSize: 8, color: mc.sub }}>Park Lane Ph2</Box>
          </Box>
          <Box sx={{ background: '#dbeafe', borderLeft: `3px solid ${mc.primary}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: mc.primary }}>Groundworks</Box>
            <Box sx={{ fontSize: 8, color: mc.sub }}>Elm Court</Box>
          </Box>
        </Box>
        {/* Thu */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#f3e8ff', borderLeft: '3px solid #7c3aed', borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#5b21b6' }}>Client meeting</Box>
            <Box sx={{ fontSize: 8, color: mc.sub }}>Riverside Dev</Box>
          </Box>
        </Box>
        {/* Fri */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#dbeafe', borderLeft: `3px solid ${mc.primary}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: mc.primary }}>Final inspection</Box>
            <Box sx={{ fontSize: 8, color: mc.sub }}>Park Lane Ph2</Box>
          </Box>
          <Box sx={{ background: '#fef3c7', borderLeft: '3px solid #d97706', borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#92400e' }}>RAMS sign-off</Box>
            <Box sx={{ fontSize: 8, color: mc.sub }}>Elm Court</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

/* ── AI Quote Review mockup ── */
const AIQuoteReviewPreview = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ fontWeight: 600, fontSize: 13 }}>AI Quote Review</Box>
        <Box sx={{ fontSize: 9, fontWeight: 600, px: 0.8, py: 0.2, borderRadius: 1, background: '#f3e8ff', color: '#7c3aed' }}>AI</Box>
      </Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
      <Box sx={{ fontSize: 11, color: mc.muted, mb: 0.5 }}>Reviewing: <span style={{ fontWeight: 600, color: mc.text }}>P-1042 · Perimeter Fencing</span></Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#dcfce7', color: '#166534' }}>3 Suggestions</Box>
        <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#fef3c7', color: '#92400e' }}>1 Warning</Box>
      </Box>
    </Box>
    {/* Review items */}
    <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ border: '1px solid #bbf7d0', borderRadius: 2, p: 1.5, background: '#f0fdf4' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Box sx={{ fontSize: 12 }}>✓</Box>
          <Box sx={{ fontSize: 11, fontWeight: 600, color: '#166534' }}>Rate check passed</Box>
        </Box>
        <Box sx={{ fontSize: 10, color: mc.sub, lineHeight: 1.5 }}>V-Mesh Weldmesh at £80/m is within the typical range (£70–£95/m) for 2.0m high panels.</Box>
      </Box>
      <Box sx={{ border: '1px solid #fde68a', borderRadius: 2, p: 1.5, background: '#fffbeb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Box sx={{ fontSize: 12 }}>⚠</Box>
          <Box sx={{ fontSize: 11, fontWeight: 600, color: '#92400e' }}>UPVC Hoarding rate high</Box>
        </Box>
        <Box sx={{ fontSize: 10, color: mc.sub, lineHeight: 1.5 }}>£160/m is above typical range (£110–£145/m). Consider reviewing or adding a note justifying the premium.</Box>
      </Box>
      <Box sx={{ border: '1px solid #bbf7d0', borderRadius: 2, p: 1.5, background: '#f0fdf4' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Box sx={{ fontSize: 12 }}>💡</Box>
          <Box sx={{ fontSize: 11, fontWeight: 600, color: '#166534' }}>Missing item suggestion</Box>
        </Box>
        <Box sx={{ fontSize: 10, color: mc.sub, lineHeight: 1.5 }}>Consider adding a line item for concrete foundations / post bases — commonly required with V-Mesh installations.</Box>
      </Box>
    </Box>
  </Box>
);

/* ── Pins on Plans mockup ── */
const PinsOnPlansPreview = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontSize: 11, color: mc.muted }}>Plans / <span style={{ color: mc.text, fontWeight: 600 }}>Site Layout — Block A</span></Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    {/* Plan area */}
    <Box sx={{ position: 'relative', height: 210, mx: 2, mt: 1.5, mb: 1, borderRadius: 2, border: `1px solid ${mc.border}`, overflow: 'hidden' }}>
      {/* SVG site layout plan */}
      <svg width="100%" height="100%" viewBox="0 0 320 210" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0 }}>
        {/* Background */}
        <rect width="320" height="210" fill="#f1f5ee" />
        {/* Site boundary */}
        <rect x="10" y="10" width="300" height="190" fill="none" stroke="#6b7280" strokeWidth="1.5" strokeDasharray="6 3" />
        {/* Road / access route along bottom */}
        <rect x="10" y="170" width="300" height="30" fill="#e5e7eb" />
        <line x1="10" y1="185" x2="310" y2="185" stroke="#9ca3af" strokeWidth="0.5" strokeDasharray="8 4" />
        <text x="155" y="188" textAnchor="middle" fontSize="7" fill="#6b7280" fontFamily="sans-serif">SITE ACCESS ROAD</text>
        {/* Pavement / kerb line */}
        <rect x="10" y="165" width="300" height="5" fill="#cbd5e1" />
        {/* Main building — Block A */}
        <rect x="22" y="25" width="110" height="80" fill="#dde8f0" stroke="#374151" strokeWidth="1.5" />
        {/* Internal walls Block A */}
        <line x1="22" y1="65" x2="132" y2="65" stroke="#374151" strokeWidth="0.8" />
        <line x1="75" y1="25" x2="75" y2="65" stroke="#374151" strokeWidth="0.8" />
        <line x1="75" y1="65" x2="75" y2="105" stroke="#374151" strokeWidth="0.8" />
        {/* Doors Block A */}
        <path d="M75,103 Q85,103 85,93" fill="none" stroke="#374151" strokeWidth="0.8" />
        <path d="M50,25 Q50,35 60,35" fill="none" stroke="#374151" strokeWidth="0.8" />
        {/* Block A label */}
        <text x="77" y="48" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#1e293b" fontFamily="sans-serif">BLOCK A</text>
        <text x="77" y="58" textAnchor="middle" fontSize="6" fill="#64748b" fontFamily="sans-serif">Office / Welfare</text>
        {/* Block B — right side */}
        <rect x="168" y="25" width="130" height="95" fill="#dde8f0" stroke="#374151" strokeWidth="1.5" />
        {/* Internal walls Block B */}
        <line x1="168" y1="70" x2="298" y2="70" stroke="#374151" strokeWidth="0.8" />
        <line x1="230" y1="25" x2="230" y2="70" stroke="#374151" strokeWidth="0.8" />
        <line x1="230" y1="70" x2="230" y2="120" stroke="#374151" strokeWidth="0.8" />
        {/* Doors Block B */}
        <path d="M230,118 Q240,118 240,108" fill="none" stroke="#374151" strokeWidth="0.8" />
        {/* Block B label */}
        <text x="233" y="50" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#1e293b" fontFamily="sans-serif">BLOCK B</text>
        <text x="233" y="61" textAnchor="middle" fontSize="6" fill="#64748b" fontFamily="sans-serif">Plant Room</text>
        {/* Compound / storage area */}
        <rect x="22" y="120" width="55" height="38" fill="#fef9c3" stroke="#ca8a04" strokeWidth="1" strokeDasharray="4 2" />
        <text x="49" y="137" textAnchor="middle" fontSize="7" fill="#854d0e" fontFamily="sans-serif">COMPOUND</text>
        <text x="49" y="147" textAnchor="middle" fontSize="6" fill="#854d0e" fontFamily="sans-serif">Storage</text>
        {/* Car park bays */}
        <rect x="85" y="120" width="70" height="38" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.8" />
        {[0, 1, 2].map(i => (
          <line key={i} x1={99 + i * 18} y1="120" x2={99 + i * 18} y2="158" stroke="#94a3b8" strokeWidth="0.6" />
        ))}
        <text x="120" y="143" textAnchor="middle" fontSize="6.5" fill="#64748b" fontFamily="sans-serif">PARKING</text>
        {/* Perimeter fencing line */}
        <rect x="168" y="128" width="130" height="30" fill="#dcfce7" stroke="#16a34a" strokeWidth="0.8" strokeDasharray="3 2" />
        <text x="233" y="141" textAnchor="middle" fontSize="6.5" fill="#166534" fontFamily="sans-serif">PERIMETER FENCING</text>
        <text x="233" y="150" textAnchor="middle" fontSize="6" fill="#166534" fontFamily="sans-serif">180m V-Mesh</text>
        {/* Dimension lines */}
        <line x1="22" y1="8" x2="132" y2="8" stroke="#6366f1" strokeWidth="0.6" markerEnd="url(#arrow)" />
        <text x="77" y="6" textAnchor="middle" fontSize="5.5" fill="#6366f1" fontFamily="sans-serif">18.5m</text>
        <line x1="7" y1="25" x2="7" y2="105" stroke="#6366f1" strokeWidth="0.6" />
        <text x="4" y="68" textAnchor="middle" fontSize="5.5" fill="#6366f1" fontFamily="sans-serif" transform="rotate(-90,4,68)">13m</text>
        {/* North arrow */}
        <polygon points="305,18 308,28 305,26 302,28" fill="#374151" />
        <text x="305" y="35" textAnchor="middle" fontSize="6" fill="#374151" fontFamily="sans-serif">N</text>
        {/* Scale bar */}
        <line x1="240" y1="8" x2="290" y2="8" stroke="#374151" strokeWidth="1" />
        <line x1="240" y1="5" x2="240" y2="11" stroke="#374151" strokeWidth="1" />
        <line x1="290" y1="5" x2="290" y2="11" stroke="#374151" strokeWidth="1" />
        <text x="265" y="6" textAnchor="middle" fontSize="5.5" fill="#374151" fontFamily="sans-serif">0     10m</text>
      </svg>
      {/* Pins */}
      <Box sx={{ position: 'absolute', top: 42, left: 60, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: mc.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.25)', cursor: 'pointer' }}>✓</Box>
      </Box>
      <Box sx={{ position: 'absolute', top: 78, left: 110, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: mc.amber, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.25)', cursor: 'pointer' }}>!</Box>
      </Box>
      <Box sx={{ position: 'absolute', top: 60, left: 220, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: mc.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.25)', cursor: 'pointer' }}>✓</Box>
      </Box>
      <Box sx={{ position: 'absolute', top: 143, left: 230, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: mc.amber, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.25)', cursor: 'pointer' }}>3</Box>
      </Box>
      {/* Selected pin popover */}
      <Box sx={{ position: 'absolute', top: 54, left: 122, background: '#fff', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', p: 1.2, width: 148, zIndex: 3, border: `1px solid ${mc.border}` }}>
        <Box sx={{ fontSize: 10, fontWeight: 600, mb: 0.3 }}>Flooring repair — Sec A</Box>
        <Box sx={{ fontSize: 9, color: mc.muted, mb: 0.5 }}>Due: 12 Mar · 2 photos</Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Box sx={{ fontSize: 8, fontWeight: 600, px: 0.8, py: 0.2, borderRadius: 1, background: '#fef3c7', color: '#92400e' }}>In Progress</Box>
        </Box>
      </Box>
    </Box>
    {/* Legend */}
    <Box sx={{ display: 'flex', gap: 2, px: 2, pb: 1.5, pt: 0.5 }}>
      {[
        { color: mc.green, label: 'Completed' },
        { color: mc.amber, label: 'In Progress' },
      ].map(l => (
        <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: l.color }} />
          <Box sx={{ fontSize: 9, color: mc.muted }}>{l.label}</Box>
        </Box>
      ))}
    </Box>
  </Box>
);

/* ── Clock In mockup ── */
const ClockInPreview = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mc.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mc.border}` }}>
      <Box sx={{ fontWeight: 600, fontSize: 13 }}>My Time</Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mc.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    {/* Clock status card */}
    <Box sx={{ mx: 2, mt: 1.5, borderRadius: 2, border: `1px solid ${mc.border}`, background: '#fafbfc', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: mc.green, boxShadow: '0 0 0 3px rgba(22,163,74,0.2)' }} />
          <Box>
            <Box sx={{ fontSize: 12, fontWeight: 600 }}>Clocked in</Box>
            <Box sx={{ fontSize: 10, color: mc.muted }}>Since 07:45 AM</Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ fontSize: 22, fontWeight: 700, color: mc.primary, lineHeight: 1 }}>4h 32m</Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', borderTop: `1px solid ${mc.border}` }}>
        <Box sx={{ flex: 1, textAlign: 'center', py: 0.8, fontSize: 11, fontWeight: 600, color: '#dc2626', cursor: 'pointer', background: '#fef2f2' }}>Clock Out</Box>
      </Box>
    </Box>
    {/* Today's entries */}
    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
      <Box sx={{ fontSize: 11, fontWeight: 600, color: mc.sub, mb: 1 }}>Today — Friday 7 Mar</Box>
    </Box>
    <Box sx={{ px: 2, pb: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr 0.6fr 0.6fr', gap: 1, py: 0.5, fontSize: 9, fontWeight: 600, color: mc.muted, textTransform: 'uppercase', borderBottom: `1px solid ${mc.border}` }}>
        <Box>Project</Box><Box>In</Box><Box>Out</Box><Box>Hours</Box>
      </Box>
      {[
        { project: 'Riverside Dev', i: '07:45', o: '—', h: '4h 32m', active: true },
      ].map(r => (
        <Box key={r.project} sx={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr 0.6fr 0.6fr', gap: 1, py: 0.8, fontSize: 11, borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
          <Box sx={{ fontWeight: 500 }}>{r.project}</Box>
          <Box>{r.i}</Box>
          <Box>{r.o}</Box>
          <Box sx={{ fontWeight: 600, color: r.active ? mc.primary : mc.text }}>{r.h}</Box>
        </Box>
      ))}
    </Box>
    {/* Week summary */}
    <Box sx={{ mx: 2, mb: 1.5, mt: 0.5, borderRadius: 2, background: '#f0f9ff', border: '1px solid #bae6fd', p: 1.2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ fontSize: 11, fontWeight: 600, color: mc.primary }}>This week</Box>
        <Box sx={{ fontSize: 13, fontWeight: 700, color: mc.primary }}>38h 17m</Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.8 }}>
        {['M', 'T', 'W', 'T', 'F'].map((d, i) => (
          <Box key={d + i} sx={{ flex: 1, textAlign: 'center', py: 0.3, borderRadius: 1, background: i < 4 ? '#dcfce7' : '#e0f2fe', fontSize: 9, fontWeight: 600, color: i < 4 ? '#166534' : mc.primary }}>
            {d}: {['8.5', '8.75', '7.5', '9.0', '4.5'][i]}h
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

const FEATURES = [
  {
    icon: AssignmentIcon,
    title: 'Project Management',
    slug: 'project-management',
    summary: 'Manage your full project lifecycle from quote through to completion. Create and track sales orders, assign team members, monitor activities, and keep every stakeholder on the same page. Duplicate projects, convert quotes to orders, and view your week at a glance with the Timeline view.',
  },
  {
    icon: TaskAltIcon,
    title: 'Activities & Task Tracking',
    slug: 'activities',
    summary: 'Create, assign, and complete activities per project. Add notes, attach photos, set due dates, and assign team members. Site managers and office staff can track progress in real time with completion states and team member updates.',
  },
  {
    icon: PhotoCameraIcon,
    title: 'Site Photos & Plan Markup',
    slug: 'site-photos',
    summary: 'Capture and upload site photos directly from your device. View construction plans with interactive pin markup — tap any location on a drawing to create linked activities with notes and photos, giving your team precise visual context.',
  },
  {
    icon: PictureAsPdfIcon,
    title: 'Digital Forms',
    slug: 'digital-forms',
    summary: 'Complete site forms on your phone — permits, inspections, safety checklists, and more. Add signatures, photos, dates, and export completed forms as branded PDFs.',
  },
  {
    icon: HealthAndSafetyIcon,
    title: 'RAMS & Safety Compliance',
    slug: 'rams',
    summary: 'Upload Risk Assessment Method Statements, capture team member signatures on-site, and track sign-off status per project. Keep your health and safety documentation audit-ready directly from the field.',
  },
  {
    icon: ShoppingCartIcon,
    title: 'Purchase Orders',
    slug: 'purchase-orders',
    summary: 'View project-linked and standalone purchase orders. Track items, monitor order status, and stay on top of procurement without leaving the app.',
  },
  {
    icon: SmartToyIcon,
    title: 'AI Assistant',
    slug: 'ai-assistant',
    summary: 'Ask your AI assistant to generate quotes, create forms, analyse project data, draft emails, and more. Attach photos or PDFs for context. The assistant takes action across your Struxor workspace — it doesn\'t just answer questions.',
  },
  {
    icon: AccessTimeIcon,
    title: 'Timesheets',
    slug: 'timesheets',
    summary: 'Clock in and out from the field. Add manual entries, edit times, and submit for approval. View weekly summaries with hours broken down by day. Managers can review and approve team member timesheets.',
  },
  {
    icon: NotificationsActiveIcon,
    title: 'Notifications',
    slug: 'notifications',
    summary: 'Stay in the loop with real-time push notifications for project updates, activity assignments, and team communications. Never miss a critical update on-site.',
  },
  {
    icon: CloudOffIcon,
    title: 'Works Offline',
    slug: 'offline',
    summary: 'Struxor syncs your project data for offline access. Create activities, upload photos, complete forms, and capture signatures even without internet — everything syncs automatically when you\'re back online.',
  },
];

export default function Features() {
  const theme = useTheme();

  return (
    <PublicPageShell>
      <Helmet>
        <title>Features — AI Assistant, Forms, Timesheets & More | Struxor</title>
        <meta
          name="description"
          content="Explore Struxor's construction software features: AI assistant, project management, digital forms, timesheets, purchase orders, RAMS, site photos with plan markup, and offline access."
        />
        <link rel="canonical" href="https://www.struxorapp.com/features" />
        <meta property="og:title" content="Features — AI Assistant, Forms, Timesheets & More | Struxor" />
        <meta property="og:description" content="Explore Struxor's construction software features: AI assistant, project management, digital forms, timesheets, purchase orders, RAMS, site photos with plan markup, and offline access." />
        <meta property="og:url" content="https://www.struxorapp.com/features" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Struxor Features',
          description: 'Explore the full feature set of Struxor construction software — AI assistant, project management, digital forms, timesheets, purchase orders, RAMS, site photos, and offline access.',
          url: 'https://www.struxorapp.com/features',
          isPartOf: { '@type': 'WebSite', name: 'Struxor', url: 'https://www.struxorapp.com' },
        })}</script>
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Everything your construction team needs — in one platform
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 680, mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' } }}>
            From first quote to final handover, Struxor keeps your office and site teams aligned with real-time project data, powerful document management, and an AI assistant that takes action.
          </Typography>
        </Container>
      </Box>

      {/* Feature Mockup Previews */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : alpha(theme.palette.background.paper, 0.5), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container sx={{ py: { xs: 6, md: 10 } }}>

          {/* Dashboard preview */}
          <FeatureMockup
            title="Dashboard"
            description="Get an at-a-glance overview of your active quotes, sales orders, upcoming activities, and project performance — all in one place."
            align="left"
          >
            <DashboardMockup />
          </FeatureMockup>

          {/* AI Assistant preview */}
          <FeatureMockup
            title="AI Assistant"
            description="Ask your assistant to generate quotes, create forms, add users, draft emails, and more. It takes action across your Struxor workspace — not just answers questions."
            align="right"
          >
            <AIMockup />
          </FeatureMockup>

          {/* Project Detail preview */}
          <FeatureMockup
            title="Project Management"
            description="Manage your full project lifecycle with quote items, activities, forms, documents, RAMS, and purchase orders — all organised in tabs."
            align="left"
          >
            <ProjectMockup />
          </FeatureMockup>

          {/* Timesheets preview */}
          <FeatureMockup
            title="Timesheets"
            description="Clock in and out from the field. View weekly summaries, submit manual entries, and manage approvals — all from one screen."
            align="right"
          >
            <TimesheetMockup />
          </FeatureMockup>

          {/* Timeline preview */}
          <FeatureMockup
            title="Timeline"
            description="View your week at a glance with a visual timeline of activities across all projects. Stay on top of what's happening on site."
            align="left"
          >
            <TimelineDashboardPreview />
          </FeatureMockup>

          {/* AI Quote Review preview */}
          <FeatureMockup
            title="AI Quote Review"
            description="Let AI review your quotes before you send them. Get rate checks, missing item suggestions, and warnings — so you catch errors before the client does."
            align="right"
          >
            <AIQuoteReviewPreview />
          </FeatureMockup>

          {/* Pins on Plans preview */}
          <FeatureMockup
            title="Pins on Plans"
            description="Tap anywhere on a construction drawing to create an activity pin. Each pin links to notes, photos, and status — giving your team precise visual context."
            align="left"
          >
            <PinsOnPlansPreview />
          </FeatureMockup>

          {/* RAMS Signature Collection preview */}
          <FeatureMockup
            title="RAMS — Collect Signatures On-Site"
            description="Send team members a RAMS directly to their phone. They review it and sign on-site — no paper, no chasing. You can see who's signed and send reminders to anyone still pending."
            align="right"
          >
            <RAMSSignatureMockup />
          </FeatureMockup>

          {/* RAMS Generator preview */}
          <FeatureMockup
            title="AI-Generated RAMS"
            description="Describe the work and let the AI draft your Risk Assessment & Method Statement — hazards, controls, PPE requirements and all. Review, edit, approve, and send for signatures in minutes."
            align="left"
          >
            <RAMSGeneratorMockup />
            <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2.5, lineHeight: 1.7 }}>
              {AI_RAMS_DISCLAIMER}
            </Typography>
          </FeatureMockup>

        </Container>
      </Box>

      {/* Feature cards grid */}
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          {FEATURES.map((feat) => {
            const Icon = feat.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={feat.slug} id={feat.slug}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 4,
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 24px rgba(0,0,0,0.08)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        background: alpha(PRIMARY, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      <Icon sx={{ fontSize: 28, color: PRIMARY }} />
                    </Box>
                    <Typography variant="h6" component="h2" fontWeight={700} sx={{ mb: 1.5 }}>
                      {feat.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {feat.summary}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* CTA */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, py: { xs: 6, md: 8 }, textAlign: 'center', color: '#fff' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={700} mb={2} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
            See it in action — start your free trial
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
            14-day free trial. Unlimited users and projects. No credit card required.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size="large"
              sx={{
                background: '#fff',
                color: PRIMARY,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                px: 4,
                '&:hover': { background: alpha('#fff', 0.9) },
              }}
            >
              Start Free Trial
            </Button>
            <Button
              component={RouterLink}
              to="/pricing"
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'rgba(255,255,255,0.4)',
                color: '#fff',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                px: 4,
                '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
              }}
            >
              View Pricing
            </Button>
          </Stack>
        </Container>
      </Box>
    </PublicPageShell>
  );
}
